import type { BuildGuideText } from "../domain/build/getProjectBuildGuide";
import {
	translateCutCount,
	translateCutLength,
	translateDimension,
	translateMaterial,
	translatePart,
	translateQuantity,
} from "./translateTerm";
import type { Dictionary } from "./types";

/**
 * Substitute `{name}` placeholders in a translated string.
 *
 * Exported because the domain emits translation keys plus their own args (see the
 * domain-layer i18n key note), so every surface that renders a domain issue needs
 * this same reduce — the plan view and the 3D drag readout each had their own copy.
 */
export function applyArgs(
	template: string,
	args: Record<string, string | number> | undefined,
): string {
	if (!args) return template;

	return Object.entries(args).reduce(
		(result, [key, value]) => result.replaceAll(`{${key}}`, String(value)),
		template,
	);
}

export function translateBuildGuideText(
	value: BuildGuideText,
	t: Dictionary,
): string {
	if (typeof value === "string") return value;
	// Quoted engine rows carry Swedish row IDs; translate each field exactly as
	// the Materials and Cut list tabs do, so guide and tabs name a row alike.
	if ("materialRow" in value) {
		const { material, dimension, quantity } = value.materialRow;
		return `${translateMaterial(material, t)} ${translateDimension(dimension, t)} • ${translateQuantity(quantity, t)}`;
	}
	if ("cutRow" in value) {
		const { part, count, cutLength } = value.cutRow;
		return `${translatePart(part, t)} • ${translateCutCount(count, t)} • ${translateCutLength(cutLength, t)}`;
	}
	return applyArgs(t[value.key], value.args);
}
